import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  createMaterial, updateMaterial, getMaterial, 
  createSKU, updateSKU, 
  getCategoriesFlat, getSites, getGroups, getUnits,
  uploadFile, createAttachment, getAttachments, deleteAttachment,
  matchMasterData
} from '@/lib/api';
import { ArrowLeft, Package, Save } from 'lucide-react';
import { BulkUploadCard } from '@/components/BulkUploadCard';
import { PhotoUploadSection } from '@/components/material/PhotoUploadSection';
import { DocumentUploadSection } from '@/components/material/DocumentUploadSection';
import { MasterDataSuggestions } from '@/components/material/MasterDataSuggestions';
import { ExistingSkuList, SkuFormSection } from '@/components/material/SkuFormSection';
import { PdfExtractor } from '@/components/material/PdfExtractor';
import { ManufacturerDropdown } from '@/components/material/ManufacturerDropdown';

export default function CreateEditMaterialPage() {
  const { materialId } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = !!materialId;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [categories, setCategories] = useState([]);
  const [sites, setSites] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [groups, setGroups] = useState([]);
  const [units, setUnits] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditMode);
  const isExternal = user?.role === 'external';
  const isAdminUser = user?.role === 'admin';

  const [material, setMaterial] = useState({
    common_title: '', common_description: '', category_node_id: '', group_id: '',
    manufacturer_name: '', manufacturer_part_number: '',
    material_width_mm: '', material_thickness_optional: '',
    material_grade_optional: '', core_diameter_optional: '',
    material_index: '', is_special: false,
  });

  const [skus, setSkus] = useState([{
    site_id: '', article_number: '', erp_article_number: '', quantity: '', uom: 'kg',
    quantity_per_roll_optional: '', number_of_rolls: '', production_date: '',
    book_value_EUR: '', list_price_EUR: '',
    customer_specific: false, compliance_flag: true, notes_internal: '', notes_external: '',
  }]);

  const [existingSkus, setExistingSkus] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [masterMatches, setMasterMatches] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const matchTimeoutRef = useRef(null);

  useEffect(() => { loadFormData(); }, []);

  const loadFormData = async () => {
    try {
      const [catRes, siteRes] = await Promise.all([getCategoriesFlat(), getSites()]);
      setCategories(catRes.data);
      setAllSites(siteRes.data);
      
      // Load units
      try {
        const unitRes = await getUnits();
        setUnits(unitRes.data || []);
      } catch {}
      
      // Load groups for admin
      let groupList = [];
      try {
        const groupRes = await getGroups();
        groupList = groupRes.data;
        setGroups(groupList);
        if (user?.group_id) {
          const myGroup = groupList.find(g => g.group_id === user.group_id);
          if (myGroup) setGroupName(myGroup.group_name);
        }
      } catch {}
      
      // Filter sites based on user role — mirrors backend _is_site_in_user_scope policy:
      //   admin without group_id → all sites
      //   admin / group_admin with group_id → only sites of their group
      //   site-bound users → only their assigned site
      let availableSites = siteRes.data;
      const isGlobalAdmin = isAdminUser && !user?.group_id;
      if (!isGlobalAdmin && user?.group_id) {
        availableSites = siteRes.data.filter(s => s.group_id === user.group_id);
      } else if (!isGlobalAdmin && !user?.group_id) {
        availableSites = [];
      }
      // Site-bound users only see their assigned site
      if (user?.site_id && !isAdminUser) {
        availableSites = availableSites.filter(s => s.site_id === user.site_id);
      }
      setSites(availableSites);
      
      // Auto-set group_id from user's group
      if (user?.group_id && !isEditMode) {
        setMaterial(prev => ({ ...prev, group_id: user.group_id }));
      }
      if (user?.site_id && !isEditMode) {
        setSkus(prev => prev.map(sku => ({ ...sku, site_id: sku.site_id || user.site_id })));
      }

      if (isEditMode) {
        const matRes = await getMaterial(materialId);
        const mat = matRes.data;
        setMaterial({
          common_title: mat.common_title || '', common_description: mat.common_description || '',
          category_node_id: mat.category_node_id || '', group_id: mat.group_id || '',
          manufacturer_name: mat.manufacturer_name || '', manufacturer_part_number: mat.manufacturer_part_number || '',
          material_width_mm: mat.material_width_mm || '', material_thickness_optional: mat.material_thickness_optional || '',
          material_grade_optional: mat.material_grade_optional || '', core_diameter_optional: mat.core_diameter_optional || '',
          material_index: mat.material_index || '', is_special: mat.is_special || false,
        });
        // For edit mode with admin, filter sites to material's group
        if (isAdminUser && mat.group_id) {
          setSites(siteRes.data.filter(s => s.group_id === mat.group_id));
        }
        setExistingSkus(mat.skus || []);
        setSkus([]);
        setExistingPhotos((mat.attachments || []).filter(a => a.type === 'photo'));
        setExistingDocuments((mat.attachments || []).filter(a => a.type !== 'photo'));
      }
    } catch (error) {
      console.error('Failed to load form data:', error);
      toast.error(t('common.loadError') || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const updateMaterialField = (field, value) => {
    setMaterial(prev => ({ ...prev, [field]: value }));
    if (field === 'manufacturer_name' || field === 'manufacturer_part_number') {
      const newMat = { ...material, [field]: value };
      debouncedMatch(newMat.manufacturer_name, newMat.manufacturer_part_number);
    }
  };

  const debouncedMatch = useCallback((name, partNum) => {
    if (matchTimeoutRef.current) clearTimeout(matchTimeoutRef.current);
    matchTimeoutRef.current = setTimeout(async () => {
      if (!name && !partNum) { setMasterMatches([]); return; }
      setMatchLoading(true);
      try {
        const res = await matchMasterData(name, partNum);
        setMasterMatches(res.data.matches || []);
      } catch { setMasterMatches([]); }
      finally { setMatchLoading(false); }
    }, 400);
  }, []);

  const applyMasterData = (master) => {
    setSelectedMaster(master);
    setMaterial(prev => ({
      ...prev,
      manufacturer_name: master.supplier_name || prev.manufacturer_name,
      manufacturer_part_number: master.supplier_article_number || prev.manufacturer_part_number,
      common_title: master.product_name || prev.common_title,
      material_width_mm: master.width_mm || prev.material_width_mm,
      material_thickness_optional: master.total_thickness_um || prev.material_thickness_optional,
    }));
    setMasterMatches([]);
    toast.success(t('masterData.applied') || 'Stammdaten angewendet');
  };

  const applyPdfData = (data) => {
    setMaterial(prev => ({
      ...prev,
      manufacturer_name: data.supplier_name || prev.manufacturer_name,
      manufacturer_part_number: data.supplier_article_number || prev.manufacturer_part_number,
      common_title: data.product_name || prev.common_title,
      material_width_mm: data.width_mm || prev.material_width_mm,
      material_thickness_optional: data.total_thickness_um || prev.material_thickness_optional,
      material_grade_optional: data.material_type || prev.material_grade_optional,
      is_special: typeof data.is_special === 'boolean' ? data.is_special : prev.is_special,
    }));
  };

  const updateSkuField = (index, field, value) => setSkus(prev => prev.map((sku, i) => i === index ? { ...sku, [field]: value } : sku));
  const addSku = () => setSkus(prev => [...prev, { site_id: '', article_number: '', erp_article_number: '', quantity: '', uom: 'kg', quantity_per_roll_optional: '', number_of_rolls: '', production_date: '', book_value_EUR: '', list_price_EUR: '', customer_specific: false, compliance_flag: true, notes_internal: '', notes_external: '' }]);
  const removeSku = (index) => { if (skus.length > 1 || isEditMode) setSkus(prev => prev.filter((_, i) => i !== index)); };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(f => f.type.startsWith('image/'));
    if (valid.length < files.length) toast.error('Nur Bilddateien erlaubt');
    setPhotos(prev => [...prev, ...valid]);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleDocSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    if (valid.length < files.length) toast.error('Nur PDF-Dateien erlaubt');
    setDocuments(prev => [...prev, ...valid]);
    if (docInputRef.current) docInputRef.current.value = '';
  };

  const removeExistingAttachment = async (attachmentId, type) => {
    try {
      await deleteAttachment(attachmentId);
      if (type === 'photo') setExistingPhotos(prev => prev.filter(a => a.attachment_id !== attachmentId));
      else setExistingDocuments(prev => prev.filter(a => a.attachment_id !== attachmentId));
      toast.success('Datei entfernt');
    } catch { toast.error('Fehler beim Entfernen'); }
  };

  const validateForm = (skuList = skus) => {
    if (!material.common_title.trim()) { toast.error(t('create.titleRequired') || 'Titel ist erforderlich'); return false; }
    if (!material.category_node_id) { toast.error(t('create.categoryRequired') || 'Kategorie ist erforderlich'); return false; }
    if (!material.manufacturer_name.trim()) { toast.error(t('create.manufacturerRequired') || 'Hersteller ist erforderlich'); return false; }
    if (!material.manufacturer_part_number.trim()) { toast.error(t('create.partNumberRequired') || 'Teilenummer ist erforderlich'); return false; }
    if (!material.group_id) { toast.error(t('create.groupRequired') || 'Unternehmensgruppe ist erforderlich'); return false; }
    // Tech spec PDF is mandatory: require either an existing document or a new upload.
    const totalDocs = (existingDocuments?.length || 0) + (documents?.length || 0);
    if (totalDocs === 0) {
      toast.error(
        t('create.techSpecRequiredError') ||
        'Bitte laden Sie ein technisches Datenblatt (PDF) hoch, bevor Sie speichern.'
      );
      return false;
    }
    for (let i = 0; i < skuList.length; i++) {
      const sku = skuList[i];
      if (!sku.site_id) { toast.error(`SKU ${i + 1}: ${t('create.siteRequired') || 'Standort ist erforderlich'}`); return false; }
      if (!sku.article_number.trim()) { toast.error(`SKU ${i + 1}: ${t('create.articleRequired') || 'Artikelnummer ist erforderlich'}`); return false; }
      if (!sku.quantity || parseFloat(sku.quantity) <= 0) { toast.error(`SKU ${i + 1}: ${t('create.quantityRequired') || 'Menge ist erforderlich'}`); return false; }
    }
    return true;
  };

  // Admin: when selecting a group, filter sites to that group
  const handleAdminGroupChange = (groupId) => {
    setMaterial(prev => ({ ...prev, group_id: groupId }));
    const filteredSites = allSites.filter(s => s.group_id === groupId);
    setSites(filteredSites);
    // Reset SKU site selections since the group changed
    setSkus(prev => prev.map(sku => ({ ...sku, site_id: '' })));
    const selectedGroup = groups.find(g => g.group_id === groupId);
    setGroupName(selectedGroup?.group_name || '');
  };

  // Auto-derive group_id from selected site
  const handleSiteChangeForSku = (index, siteId) => {
    updateSkuField(index, 'site_id', siteId);
    // Auto-set group_id from the selected site (for non-admin users, the backend enforces this anyway)
    if (!isAdminUser) {
      const selectedSite = sites.find(s => s.site_id === siteId);
      if (selectedSite) {
        setMaterial(prev => ({ ...prev, group_id: selectedSite.group_id }));
      }
    }
  };

  const handleSubmit = async () => {
    // Defensive: for site-bound users (regular users / site managers / externals),
    // always force the SKU site_id to their own site so the backend "site in scope"
    // check passes regardless of UI state.
    if (user?.site_id && !isAdminUser) {
      setSkus(prev => prev.map(sku => ({ ...sku, site_id: user.site_id })));
    }
    // Ensure manufacturer name is trimmed (free-text typed values may carry whitespace).
    const submittingSkus = (user?.site_id && !isAdminUser)
      ? skus.map(sku => ({ ...sku, site_id: user.site_id }))
      : skus;

    if (!validateForm(submittingSkus)) return;
    setSaving(true);
    try {
      const materialPayload = {
        ...material,
        material_width_mm: material.material_width_mm ? parseFloat(material.material_width_mm) : null,
        material_thickness_optional: material.material_thickness_optional ? parseFloat(material.material_thickness_optional) : null,
        core_diameter_optional: material.core_diameter_optional ? parseFloat(material.core_diameter_optional) : null,
      };

      let matId = materialId;
      if (isEditMode) {
        await updateMaterial(materialId, materialPayload);
        toast.success(t('create.materialUpdated') || 'Material aktualisiert');
      } else {
        const res = await createMaterial(materialPayload);
        matId = res.data.material_id;
        toast.success(t('create.materialCreated') || 'Material erstellt');
      }

      for (const sku of submittingSkus) {
        if (sku.site_id && sku.article_number && sku.quantity) {
          await createSKU({
            material_id: matId, site_id: sku.site_id, article_number: sku.article_number,
            erp_article_number: sku.erp_article_number || null,
            quantity: parseFloat(sku.quantity), uom: sku.uom,
            quantity_per_roll_optional: sku.quantity_per_roll_optional ? parseFloat(sku.quantity_per_roll_optional) : null,
            number_of_rolls: sku.number_of_rolls ? parseInt(sku.number_of_rolls) : null,
            production_date: sku.production_date || null,
            book_value_kEUR: sku.book_value_EUR ? parseFloat(sku.book_value_EUR) / 1000 : null,
            list_price_kEUR: sku.list_price_EUR ? parseFloat(sku.list_price_EUR) / 1000 : null,
            customer_specific: sku.customer_specific,
            compliance_flag: sku.compliance_flag,
            notes_internal: sku.notes_internal || null, notes_external: sku.notes_external || null,
          });
        }
      }

      for (const file of photos) {
        try {
          const uploadRes = await uploadFile(file);
          await createAttachment({ material_id: matId, type: 'photo', file_url: uploadRes.data.file_url, thumbnail_url: uploadRes.data.thumbnail_url, blur_hash: uploadRes.data.blur_hash });
        } catch (e) { console.error('Photo upload failed:', e); }
      }

      for (const file of documents) {
        try {
          const uploadRes = await uploadFile(file);
          await createAttachment({ material_id: matId, type: 'data_sheet', file_url: uploadRes.data.file_url });
        } catch (e) { console.error('Document upload failed:', e); }
      }

      navigate(`/material/${matId}`);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || t('create.saveError') || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3" />
            <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="create-edit-material-page">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('common.back') || 'Zuruck'}
          </Button>
          <Separator orientation="vertical" className="h-4" />
          <h1 className="font-manrope text-2xl font-bold" data-testid="page-title">
            {isEditMode ? (t('create.editTitle') || 'Material bearbeiten') : (t('create.newTitle') || 'Neues Material einstellen')}
          </h1>
        </div>

        {!isEditMode && <BulkUploadCard />}

        {/* AI PDF Extraction */}
        <Card className="mb-6 border-violet-200/50 dark:border-violet-800/50" data-testid="pdf-extraction-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              Daten aus PDF extrahieren (KI)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PdfExtractor onExtracted={applyPdfData} />
          </CardContent>
        </Card>

        <PhotoUploadSection
          existingPhotos={existingPhotos} photos={photos} photoInputRef={photoInputRef}
          onPhotoSelect={handlePhotoSelect} onRemoveNew={(i) => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
          onRemoveExisting={removeExistingAttachment} t={t}
        />

        {/* Material Info Section */}
        <Card className="mb-6" data-testid="material-info-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Package className="h-5 w-5 text-orange-500" />
              {t('create.materialInfo') || 'Material-Informationen'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="common_title">{t('create.title') || 'Titel'} *</Label>
                <Input id="common_title" value={material.common_title}
                  onChange={(e) => updateMaterialField('common_title', e.target.value)}
                  placeholder={t('create.titlePlaceholder') || 'z.B. PE-Folie 500mm transparent'}
                  data-testid="material-title-input" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="common_description">{t('create.description') || 'Beschreibung'}</Label>
                <Textarea id="common_description" value={material.common_description}
                  onChange={(e) => updateMaterialField('common_description', e.target.value)}
                  placeholder={t('create.descPlaceholder') || 'Detaillierte Beschreibung des Materials...'} rows={3}
                  data-testid="material-description-input" />
              </div>
              <div>
                <Label>{t('filter.category') || 'Kategorie'} *</Label>
                <Select value={material.category_node_id} onValueChange={(v) => updateMaterialField('category_node_id', v)}>
                  <SelectTrigger data-testid="category-select"><SelectValue placeholder={t('create.selectCategory') || 'Kategorie wahlen'} /></SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.node_id} value={cat.node_id}>{cat.parent_node_id ? '  \u2514 ' : ''}{cat.node_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Group selector - global admin (no group_id) can pick any group; scoped admins are locked to their group */}
              {isAdminUser && !user?.group_id ? (
                <div>
                  <Label>{t('create.companyGroup') || 'Unternehmensgruppe'} *</Label>
                  <Select value={material.group_id} onValueChange={handleAdminGroupChange}>
                    <SelectTrigger data-testid="group-select"><SelectValue placeholder="Gruppe auswählen" /></SelectTrigger>
                    <SelectContent>
                      {groups.map(g => (
                        <SelectItem key={g.group_id} value={g.group_id}>{g.group_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Als Platform-Admin können Sie Material für jede Gruppe erstellen
                  </p>
                </div>
              ) : user?.group_id ? (
                <div>
                  <Label>{t('create.companyGroup') || 'Unternehmensgruppe'}</Label>
                  <Input value={groupName || 'Wird automatisch zugewiesen'} disabled className="bg-muted" data-testid="group-display" />
                </div>
              ) : null}
              <div className="relative">
                <Label htmlFor="manufacturer_name">{t('filter.manufacturer') || 'Hersteller'} *</Label>
                <ManufacturerDropdown
                  value={material.manufacturer_name}
                  onChange={(val) => updateMaterialField('manufacturer_name', val)}
                />
              </div>
              <div className="relative">
                <Label htmlFor="manufacturer_part_number">{t('create.partNumber') || 'Teilenummer'} *</Label>
                <Input id="manufacturer_part_number" value={material.manufacturer_part_number}
                  onChange={(e) => updateMaterialField('manufacturer_part_number', e.target.value)}
                  placeholder="z.B. AD225" data-testid="part-number-input" />
              </div>

              <MasterDataSuggestions matches={masterMatches} loading={matchLoading}
                selectedMaster={selectedMaster} onApply={applyMasterData}
                onClear={() => setSelectedMaster(null)} t={t} />

              <div>
                <Label htmlFor="material_width_mm">{t('filter.width') || 'Breite'} (mm)</Label>
                <Input id="material_width_mm" type="number" value={material.material_width_mm}
                  onChange={(e) => updateMaterialField('material_width_mm', e.target.value)} placeholder="500" data-testid="width-input" />
              </div>
              <div>
                <Label htmlFor="material_thickness">{t('create.thickness') || 'Dicke'} (mm)</Label>
                <Input id="material_thickness" type="number" value={material.material_thickness_optional}
                  onChange={(e) => updateMaterialField('material_thickness_optional', e.target.value)} placeholder="0.05" data-testid="thickness-input" />
              </div>
              <div>
                <Label htmlFor="material_grade">{t('create.grade') || 'Material-Gute'}</Label>
                <Input id="material_grade" value={material.material_grade_optional}
                  onChange={(e) => updateMaterialField('material_grade_optional', e.target.value)} placeholder="z.B. Food Grade" data-testid="grade-input" />
              </div>
              <div>
                <Label htmlFor="core_diameter">{t('create.coreDiameter') || 'Kerndurchmesser'} (mm)</Label>
                <Input id="core_diameter" type="number" value={material.core_diameter_optional}
                  onChange={(e) => updateMaterialField('core_diameter_optional', e.target.value)} placeholder="76" data-testid="core-diameter-input" />
              </div>
              <div>
                <Label htmlFor="material_index">{t('create.materialIndex') || 'Material-Index'}</Label>
                <Input id="material_index" value={material.material_index}
                  onChange={(e) => updateMaterialField('material_index', e.target.value)} placeholder="z.B. MI-2026-001" data-testid="material-index-input" />
              </div>
              <div className="flex items-center gap-3 md:col-span-2 pt-2">
                <input
                  type="checkbox"
                  id="is_special"
                  checked={material.is_special}
                  onChange={(e) => updateMaterialField('is_special', e.target.checked)}
                  className="h-5 w-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer"
                  data-testid="is-special-checkbox"
                />
                <Label htmlFor="is_special" className="cursor-pointer text-sm font-medium">
                  Spezialmaterial
                </Label>
                <span className="text-xs text-muted-foreground">
                  Markiert dieses Material als Spezialmaterial
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <DocumentUploadSection
          existingDocuments={existingDocuments} documents={documents} docInputRef={docInputRef}
          onDocSelect={handleDocSelect} onRemoveNew={(i) => setDocuments(prev => prev.filter((_, idx) => idx !== i))}
          onRemoveExisting={removeExistingAttachment} t={t}
        />

        {isEditMode && existingSkus.length > 0 && (
          <ExistingSkuList skus={existingSkus} t={t} />
        )}

        <SkuFormSection skus={skus} sites={sites} isEditMode={isEditMode} isExternal={isExternal}
          onUpdateField={updateSkuField} onSiteChange={handleSiteChangeForSku} onAdd={addSku} onRemove={removeSku} units={units} t={t} />

        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="cancel-button">
            {t('common.cancel') || 'Abbrechen'}
          </Button>
          <Button onClick={handleSubmit} disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 min-w-[160px]" data-testid="save-material-button">
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                {t('common.saving') || 'Speichern...'}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                {isEditMode ? (t('common.save') || 'Speichern') : (t('create.publish') || 'Veroffentlichen')}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
